/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import ShowChartIcon from "@mui/icons-material/ShowChart";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import dataTableData from "layouts/ecommerce/orders/order-list/data/dataTableData";
import RoomIcon from "@mui/icons-material/Room";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { API_HOHOLOK } from "utils";

function RekapProgresList() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPelaksana, setSelectedPelaksana] = useState(null);

  const openMenu = (event) => setMenu(event.currentTarget);
  const closeMenu = () => setMenu(null);

  const handleOpenDialog = (row) => {
    setSelectedPelaksana(row);
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPelaksana(null);
  };

  // Fungsi untuk navigasi ke halaman kurva target realisasi proyek
  const handleKurvaS = (idProyek) => {
    navigate(`/pages/proyek/kurva-target-realisasi-proyek/${idProyek}`);
  };

  function getCurrentDate() {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0"); // bulan dimulai dari 0
    const day = String(today.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  // Fungsi untuk memformat tanggal
  function formatTanggal(tanggal) {
    if (!tanggal) return "-";
    const date = new Date(tanggal);
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  useEffect(() => {
    setLoading(true);
    const tanggalHariIni = getCurrentDate();
    console.log(tanggalHariIni);
    axios
      .get(
        `${API_HOHOLOK}laporan-harian/rekap-progres?tanggal=${tanggalHariIni}`
      )
      .then((res) => {
        if (res.data && res.data.success) {
          console.log("data ku ", res.data.data);
          setData(res.data.data);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const columns = useMemo(
    () => [
      { Header: "No", accessor: "no", width: 50 },
      {
        Header: "Nama Proyek",
        accessor: "nama_proyek",
        width: 200,
        minWidth: 200,
        maxWidth: 200,
        Cell: ({ value }) => (
          <MDTypography
            variant="caption"
            color="text"
            fontWeight="medium"
            sx={{
              wordWrap: "break-word",
              whiteSpace: "normal",
              lineHeight: 1.4,
              width: "200px",
              maxWidth: "200px",
              minWidth: "200px",
              display: "block",
            }}
          >
            {value}
          </MDTypography>
        ),
      },
      { Header: "Informasi Proyek", accessor: "info_proyek" },
      { Header: "Pelaksana", accessor: "pelaksana" },
      { Header: "Nilai Proyek", accessor: "nilai_proyek" },
      { Header: "Target (%)", accessor: "target_persen" },
      { Header: "Realisasi (%)", accessor: "realisasi_persen" },
      { Header: "Deviasi (%)", accessor: "deviasi" },
      { Header: "Total Tenaga Kerja", accessor: "total_tenaga_kerja" },
      { Header: "Koordinat", accessor: "koordinat" },
      { Header: "Kurva S", accessor: "kurva_s", width: 100 },
    ],
    []
  );

  const rows = useMemo(
    () =>
      data.map((row, idx) => ({
        no: idx + 1,
        nama_proyek: row.nama_proyek,
        info_proyek: (
          <MDBox>
            <MDTypography variant="caption" color="text" fontWeight="medium">
              📅 Mulai: {formatTanggal(row.tanggal_awal_proyek)}
            </MDTypography>
            <br />
            <MDTypography variant="caption" color="text" fontWeight="medium">
              🏁 Selesai: {formatTanggal(row.tanggal_akhir_proyek)}
            </MDTypography>
            <br />
            <MDTypography variant="caption" color="text" fontWeight="medium">
              📊 Pengecekan: {formatTanggal(row.tanggal_pengecekan)}
            </MDTypography>
            <br />
            <MDTypography
              variant="caption"
              color="info"
              fontWeight="bold"
              sx={{
                backgroundColor: "#e3f2fd",
                padding: "2px 6px",
                borderRadius: "4px",
                display: "inline-block",
              }}
            >
              🗓️ Minggu ke-{row.minggu_ke_berdasarkan_tanggal}
            </MDTypography>
          </MDBox>
        ),
        pelaksana: (
          <MDButton
            size="small"
            color="info"
            variant="outlined"
            onClick={() => handleOpenDialog(row)}
          >
            Lihat Pelaksana
          </MDButton>
        ),
        nilai_proyek: row.nilai_proyek?.toLocaleString("id-ID"),
        target_persen: Number(row.target_persen).toFixed(2),
        realisasi_persen: (
          <MDBox display="flex" alignItems="center" minWidth={120}>
            <MDProgress
              value={Math.max(
                0,
                Math.min(100, Number(row.realisasi_persen) || 0)
              )}
              color={
                row.realisasi_persen >= row.target_persen ? "success" : "info"
              }
              sx={{ width: "100%", mr: 1, height: 10, borderRadius: 5 }}
            />
            <MDTypography variant="caption" color="text" ml={1}>
              {Number(row.realisasi_persen).toFixed(2)}%
            </MDTypography>
          </MDBox>
        ),
        deviasi: (
          <MDTypography color={row.deviasi < 0 ? "error" : "success"}>
            {Number(row.deviasi).toFixed(2)}%
          </MDTypography>
        ),
        total_tenaga_kerja: row.total_tenaga_kerja,
        koordinat:
          row.longitude && row.latitude ? (
            <MDBox display="flex" alignItems="center" gap={1}>
              <RoomIcon color="primary" fontSize="small" />
              <span>
                {row.longitude}, {row.latitude}
              </span>
            </MDBox>
          ) : (
            <MDTypography color="text" fontSize={12}>
              -
            </MDTypography>
          ),
        kurva_s: (
          <MDButton
            size="small"
            color="info"
            variant="contained"
            onClick={() => handleKurvaS(row.id_proyek)}
            startIcon={<ShowChartIcon />}
            sx={{
              backgroundColor: "#1976d2",
              color: "white",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            Kurva S
          </MDButton>
        ),
      })),
    [data]
  );

  const renderMenu = (
    <Menu
      anchorEl={menu}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={Boolean(menu)}
      onClose={closeMenu}
      keepMounted
    >
      <MenuItem onClick={closeMenu}>Status: Paid</MenuItem>
      <MenuItem onClick={closeMenu}>Status: Refunded</MenuItem>
      <MenuItem onClick={closeMenu}>Status: Canceled</MenuItem>
      <Divider sx={{ margin: "0.5rem 0" }} />
      <MenuItem onClick={closeMenu}>
        <MDTypography variant="button" color="error" fontWeight="regular">
          Remove Filter
        </MDTypography>
      </MenuItem>
    </Menu>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <MDButton variant="gradient" color="info">
            Proyek
          </MDButton>
          <MDBox display="flex">
            <MDButton
              variant={menu ? "contained" : "outlined"}
              color="dark"
              onClick={openMenu}
            >
              filters&nbsp;
              <Icon>keyboard_arrow_down</Icon>
            </MDButton>
            {renderMenu}
            <MDBox ml={1}>
              <MDButton variant="outlined" color="dark">
                <Icon>description</Icon>
                &nbsp;export csv
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
        <Card>
          {loading ? (
            <MDBox p={4} textAlign="center">
              <MDTypography>Loading...</MDTypography>
            </MDBox>
          ) : (
            <DataTable table={{ columns, rows }} entriesPerPage canSearch />
          )}
        </Card>
      </MDBox>
      <Footer />
      {/* Dialog untuk info pelaksana */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Info Pelaksana Proyek</DialogTitle>
        <DialogContent>
          {selectedPelaksana && (
            <MDBox>
              <MDTypography variant="subtitle2" fontWeight="bold">
                Kontraktor Pelaksana:
              </MDTypography>
              <MDTypography mb={2}>
                {selectedPelaksana.nama_kontraktor_pelaksana || "-"}
              </MDTypography>
              <MDTypography variant="subtitle2" fontWeight="bold">
                Konsultan Perencana:
              </MDTypography>
              <MDTypography mb={2}>
                {selectedPelaksana.nama_konsultan_perencana || "-"}
              </MDTypography>
              <MDTypography variant="subtitle2" fontWeight="bold">
                Konsultan Pengawas:
              </MDTypography>
              <MDTypography>
                {selectedPelaksana.nama_konsultan_pengawas || "-"}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Tutup
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default RekapProgresList;
